
import Vue from "vue";
import { getStore } from '@/store/registry';
import * as names from "@/store/names";
import router from '@/router/index';
import public_routes from '@/router/public';

export const EventBus = new Vue({
  data: {
    processes: 0,
    ended: 0,
  },
  methods: {
    isAuthenticated() {
      return this.$msal.isAuthenticated();
    },
    signIn() {
      if (!this.$msal.isAuthenticated()) {
        this.$msal.signIn();
      }
    },
    signOut() {
      if (this.$msal.isAuthenticated()) {
        this.$msal.signOut();
      }
    },
    startLoad() {
      if (this.processes == 0) this.$emit("loadStarted");
      this.processes += 1;
      this.$emit("increment", this.processes);
    },
    endLoad() {
      this.ended += 1;
      this.$emit("decrement", this.ended);
      if (this.processes <= this.ended) {
        this.processes = 0;
        this.ended = 0;
      }
      if (this.processes == 0) this.$emit("loadFinished");
    },
  },
});

// SEC-3 fase 2: el token ya no vive en localStorage — se recibe por parámetro
// (los llamadores lo leen del store, que lo mantiene solo en memoria).
export function isValidJwt(jwt) {
  if (!jwt || jwt.split(".").length < 3) {
    return false;
  }
  const data = JSON.parse(atob(jwt.split(".")[1]));
  const exp = new Date(data.exp * 1000);
  const now = new Date();
  return now < exp;
}

export function expirationTime(jwt) {
  if (!jwt || jwt.split(".").length < 3) {
    return 0;
  }
  const data = JSON.parse(atob(jwt.split(".")[1]));
  const exp = new Date(data.exp * 1000);
  const now = new Date();
  return Math.abs(now - exp);
}

export function expiresAt(jwt) {
  if (!jwt || jwt.split(".").length < 3) {
    return 0;
  }
  const data = JSON.parse(atob(jwt.split(".")[1]));
  const exp = new Date(data.exp * 1000);
  return exp;
}

export function generateUniqueId() {
  return (
    Math.random()
      .toString(36)
      .substring(2) + Date.now().toString(36)
  );
}

export function areEqualsObjects(object1, object2) {
  Object.entries(object1).forEach((key, value) => {
    if (Object.prototype.hasOwnProperty.call(object2, key)) return false;
    if (object2[key] != value) return false;
  });
  return JSON.stringify(object1) == JSON.stringify(object2);
}

// Completa en el store los EgressProfileMatter/Matter faltantes a partir de
// una lista de ids de EgressProfileMatter (ej. prerequisitos: pueden apuntar
// a un EgressProfileMatter de OTRO perfil de egreso, que no llega con el
// FETCH_MATTERS/FETCH_EGRESS_PROFILES_MATTERS filtrado por egress_profile).
// Los trae en batch (id__in), 1 sola vez por grupo, en vez de que cada vista
// que muestra una referencia a estas entidades reimplemente el mismo fetch.
export const fetchMissingEgressProfileMattersAndMatters = (egress_profile_matter_ids) => {
  const ids = [...new Set(egress_profile_matter_ids)].filter(Boolean);
  if (ids.length == 0) return Promise.resolve();
  const egress_profiles_matters = getStore().getters[names.EGRESS_PROFILES_MATTERS];
  const matters = getStore().getters[names.MATTERS];
  const missing_epm_ids = ids.filter(
    (id) => !egress_profiles_matters.find((x) => x.id == id)
  );
  const fetchMissingMatters = (epms) => {
    const missing_matter_ids = [
      ...new Set(
        epms
          .map((epm) => epm.matter)
          .filter((matter_id) => !matters.find((x) => x.id == matter_id))
      ),
    ];
    if (missing_matter_ids.length == 0) return Promise.resolve();
    return getStore().dispatch(names.FETCH_MATTERS_BY_IDS, missing_matter_ids);
  };
  if (missing_epm_ids.length > 0) {
    return getStore()
      .dispatch(names.FETCH_EGRESS_PROFILE_MATTERS_BY_IDS, missing_epm_ids)
      .then(fetchMissingMatters);
  }
  return fetchMissingMatters(
    ids
      .map((id) => egress_profiles_matters.find((x) => x.id == id))
      .filter(Boolean)
  );
};

export function toast(message) {
  Vue.swal
    .mixin({
      toast: true,
      position: "bottom-start",
      showConfirmButton: false,
      timer: 3000,
      title: message,
    })
    .fire();
}

export function customToast(message, position, timer) {
  Vue.swal
    .mixin({
      toast: true,
      position: position,
      showConfirmButton: false,
      timer: timer,
      title: message,
    })
    .fire();
}

/**
 * Muestra una alerta de carga con un mensaje personalizado.
 *
 * @param {string} [title] - El título de la alerta (por defecto: "Subiendo archivo...").
 * @param {string} [text] - El texto descriptivo de la alerta (por defecto: "Por favor, espera mientras se sube el archivo.").
 * @param {boolean} [showCloseButton] - Si es true, muestra un botón (x) para cerrar la alerta manualmente (por defecto: false).
 * @param {Function} [onClose] - Callback ejecutado cuando la alerta se cierra, sin importar la causa
 *   (botón x, tecla, o this.$swal.close() invocado desde cualquier parte). Útil para sincronizar
 *   estado (p.ej. liberar un flag de "loading") con el cierre real del swal.
 * @returns {void}
 * Función necesaria para finalizar la alerta
 * @function  this.$swal.close()
*/
export function showLoadingAlert(title = "Subiendo archivo...", text = "Por favor, espera mientras se sube el archivo.", showCloseButton = false, onClose = null) {
  Vue.swal
    .fire({
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      focusConfirm: false,
      showCloseButton,
      onBeforeOpen: () => {
        Vue.swal.showLoading();
      },
      onClose,
    });
}

/**
 * Redondea un número a una cantidad específica de decimales y lo devuelve como número.
 * Usa una corrección de precisión con Number.EPSILON para evitar errores comunes de punto flotante.
 *
 * @param {number|string} value        El número (o cadena numérica) a redondear.
 * @param {number} [decimals=2]        Cantidad de decimales a los que se desea redondear.
 * @returns {number}                   El número redondeado con la cantidad de decimales especificada.
 */
export function round(value, decimals = 2) {
  const number = parseFloat(value);
  if (isNaN(number)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round((number + Number.EPSILON) * factor) / factor;
}

/**
 * Trunca un número hasta un número determinado de decimales (sin redondeo)
 * y lo devuelve como una cadena con exactamente esa cantidad de decimales.
 *
 * @param {number} number    El número a truncar.
 * @param {number} [decimals=2]  Cantidad de decimales deseada.
 * @returns {string}  El número truncado en formato string con 'decimals' decimales.
 */
export function truncateToDecimals(number, decimals = 2) {
  const factor = Math.pow(10, decimals);
  // toPrecision(12) neutraliza ruido de punto flotante (ej: 8.7*100=869.999... → 870)
  // sin redondear valores legítimos (ej: 8.679*100=867.9 → sigue siendo 867.9)
  const corrected = Number((number * factor).toPrecision(12));
  const truncated = Math.trunc(corrected) / factor;
  return parseFloat(truncated.toFixed(decimals));
}



export function base64UrlToBytes(base64Url) {
  const padding = '='.repeat((4 - base64Url.length % 4) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const bytes = window.atob(base64).split('').map(char => char.charCodeAt(0));
  return new Uint8Array(bytes);
}

/**
 * Determina si un hostname corresponde al dominio genérico de klearning.cl
 * (sin subdominio de institución propio): producción, su alias www, o staging.
 * @param {string} hostname - Típicamente window.location.hostname.
 * @returns {boolean}
 */
export const isGenericKlearningHost = (hostname) => {
  return (
    hostname === "klearning.cl" ||
    hostname === "www.klearning.cl" ||
    hostname === "dev.klearning.cl"
  );
};

/**
 * Construye la URL del subdominio de una institución bajo *.klearning.cl,
 * preservando el sufijo "-dev" cuando se navega desde dev.klearning.cl.
 * @param {string} subdomain - Institution.subdomain de la institución destino.
 * @param {Object} [options]
 * @param {string} [options.redirect] - Ruta interna ("/algo") a la que ir tras aterrizar en
 *   el destino. Viaja como PATH real de la URL, NO como query "redirect": así la navegación
 *   inicial de vue-router cae directo en la vista, sin depender de leer la query y hacer un
 *   push tras onReady (que competía con el redirect de bootstrap '/' -> '/home' y se perdía).
 *   Solo se aceptan rutas internas ("/algo", nunca "//host") para no abrir un redirect externo.
 * @param {boolean} [options.sessionHint] - Agrega "kl_session_redirect=1": el hint de sesión
 *   vive en localStorage, que no cruza de origin, así que main.js necesita esta señal
 *   explícita para intentar restaurar la sesión al aterrizar en el subdominio.
 * @returns {string|null} La URL destino, o null si la institución no tiene subdominio.
 */
export const buildSubdomainUrl = (subdomain, { redirect, sessionHint } = {}) => {
  if (!subdomain) return null;
  const isDev = window.location.hostname === "dev.klearning.cl";
  const targetSubdomain = isDev ? `${subdomain}-dev` : subdomain;
  const base = `https://${targetSubdomain}.klearning.cl`;
  const path = (typeof redirect === "string" && /^\/(?!\/)/.test(redirect)) ? redirect : "/";
  const url = new URL(path, base);
  if (sessionHint) url.searchParams.set("kl_session_redirect", "1");
  return url.toString();
};

export const backend_usecured_routes = [
  '/token/',
  '/token/refresh/',
  '/login/',
  '/authentication/send_reset_code/',
  '/authentication/reset-password/',
  '/authentication/azure/institutions/',
  '/authentication/azure/login/',
  '/authentication/azure/callback/',
  '/authentication/azure/check/',
  '/authentication/login-institutions/',
  // Deshabilitado: logo por subdominio (ver NavBar.vue loadSubdomainLogo).
  // '/authentication/subdomain-logo/',
  '/common/contact-data/',
  '/common/verify-recaptcha/',
  '/health_check',
  '/authentication/sso/institutions/',
  '/authentication/sso/login/'
];

/**
 * Construye una cadena de parámetros URL a partir de un objeto de payload.
 * 
 * @param {Object} payload - El objeto que contiene los parámetros a convertir en una cadena de URL.
 * @param {Array|string|number|boolean} payload[key] - Cada clave puede tener un valor que sea un array, string, número o booleano.
 * (`allData` se ignora desde 2026-09-20: el parámetro all_data está retirado; los consumidores paginan con restful.GetAll.)
 *
 * Omite claves de control que no son filtros del backend (`force`) y valores null/undefined o vacíos.
 *
 * @returns {string|null} - La cadena de parámetros URL construida, o `null` si no se serializó ningún filtro (el caller debe abortar el fetch).
 */
export const buildUrlParams = (payload) => {
  const control_keys = ['force'];
  let params = [];
  Object.keys(payload || {}).forEach(key => {
    if (control_keys.includes(key)) return;
    if (Array.isArray(payload[key])) {
      if (payload[key].length > 0) {
        params.push(`${key}=${payload[key].join(",")}`);
      }
    } else if (payload[key] != null && payload[key] !== '') {
      params.push(`${key}=${payload[key]}`);
    }
  });
  if (params.length === 0) return null;
  return params.join('&');
};

/**
 * Filtra si en la cadena A se incluye algo de la cadena B.
 * Este método normaliza las cadenas para eliminar tildes y caracteres especiales.
 * @param {string} A - La primera cadena a comparar.
 * @param {string} B - La cadena que se desea buscar dentro de A.
 * @returns {boolean} - Devuelve true si A contiene B, false en caso contrario.
 */
export const filtered = (A, B) => {
  if (String(A).normalize("NFD")
    .replace(/([aeio])\u0301|(u)[\u0301\u0308]/gi, "$1$2")
    .normalize()
    .trim()
    .toLowerCase()
    .includes(String(B).normalize("NFD")
      .replace(/([aeio])\u0301|(u)[\u0301\u0308]/gi, "$1$2")
      .normalize()
      .trim()
      .toLowerCase())) return true
  else return false
};

/**
 * Compara si la cadena A es igual a la cadena B.
 * Este método normaliza las cadenas para eliminar tildes y caracteres especiales.
 * @param {string} A - La primera cadena a comparar.
 * @param {string} B - La segunda cadena a comparar.
 * @returns {boolean} - Devuelve true si A es igual a B, false en caso contrario.
 */
export const equals = (A, B) => {
  if (String(A).normalize("NFD")
    .replace(/([aeio])\u0301|(u)[\u0301\u0308]/gi, "$1$2")
    .normalize()
    .trim()
    .toLowerCase() ==
    String(B).normalize("NFD")
      .replace(/([aeio])\u0301|(u)[\u0301\u0308]/gi, "$1$2")
      .normalize()
      .trim()
      .toLowerCase()) return true
  else return false
};

/**
 * Obtiene el nombre visible de una tabla basada en un nombre alternativo y la escuela del usuario.
 * @param {string} table_name - El nombre de la tabla a verificar.
 * @param {boolean} isPlural - Indica si el nombre es plural.
 * @param {string} original_name - El nombre original de la tabla.
 * @returns {string} - El nombre visible basado en la configuración de la escuela del usuario, o el nombre original.
 */
export const getVisibleNames = (table_name, isPlural, original_name, aplly_article = false) => {
  if (!getStore().state.user) return original_name;
  if (!getStore().state.user.school) return original_name;
  const alternative_name = getStore().state.alternative_names.alternative_names.find(
    (x) => x.table_name == table_name && x.school == getStore().state.user.school
  );
  if (!alternative_name) return original_name;
  if (isPlural) {
    const text_name = alternative_name ?
      alternative_name.visible_name_plural ?
        alternative_name.visible_name_plural :
        alternative_name.verbose_name_plural :
      original_name
    if (aplly_article && alternative_name.plural_article != '') return alternative_name.plural_article + ' ' + text_name
    else
      return text_name;
  }
  else {
    const text_name = alternative_name ?
      alternative_name.visible_name ?
        alternative_name.visible_name :
        alternative_name.verbose_name :
      original_name
    if (aplly_article && alternative_name.singular_article != '') return alternative_name.singular_article + ' ' + text_name
    else
      return text_name;
  }
};

/**
 * Verifica si el usuario tiene permisos sobre un objeto basado en el nombre de la tabla, método y, opcionalmente, un ID.
 * @param {string} table_name - El nombre de la tabla a verificar.
 * @param {string} methods - El tipo de operación a verificar ('add', 'change', 'delete', 'special').
 * @param {number|null|String} [id=null] - El ID del objeto a verificar (opcional) (si se pasa "at_least_one": Con que tenga una coincidencia en la tabla respectiva, retornará true).
 * @returns {boolean} - Devuelve true si el usuario tiene los permisos necesarios, false en caso contrario.
 */
export const hasObjectPermission = (table_name, methods, id = null) => {
  const table = getStore().state.positions.user_table_access.find(x => equals(x.table_name, table_name))
  if (table) {
    if (equals(methods, 'add') && table.can_create) return true
    if (equals(methods, 'change') && (equals(table.can_update, 'all') || (id != null && table.can_update.split(",").map(Number).includes(id)) || (equals(id, 'at_least_one') && table.can_update != ''))) return true
    if (equals(methods, 'delete') && (equals(table.can_delete, 'all') || (id != null && table.can_delete.split(",").map(Number).includes(id)) || (equals(id, 'at_least_one') && table.can_delete != ''))) return true
    if (equals(methods, 'special') && (equals(table.can_do_special_behavior, 'all') || (id != null && table.can_do_special_behavior.split(",").map(Number).includes(id)) || (equals(id, 'at_least_one') && table.can_do_special_behavior != ''))) return true
  } else return false
  return false
}

// En el programa de asignatura, superusuario y directivos institucionales borran cualquier
// título; el resto según /egress-profile-matter-descriptor/deletable/. Debe coincidir con
// TITLE_DELETE_ADMIN_GROUPS de kl-backend mesh/descriptor_titles.py.
export const MATTER_DESCRIPTOR_TITLE_ADMIN_GROUPS = [
  'DIRECTIVOS ACADÉMICOS INSTITUCIONALES',
]

export const isMatterDescriptorTitleAdmin = (user) => {
  if (!user) return false
  if (user.is_superuser) return true
  return MATTER_DESCRIPTOR_TITLE_ADMIN_GROUPS.some(group => (user.group_names || []).includes(group))
}

/**
 * Verifica si el usuario tiene permisos específicos.
 * @param {string|null} [permission=null] - El permiso a verificar (opcional).
 * @returns {boolean} - Devuelve true si el usuario tiene los permisos, false en caso contrario.
 */
export const hasPermissions = (permission = null) => {
  if (!getStore().getters.isLoged && router.currentRoute.name != "Welcome") {
    getStore().commit(names.MUTATE_VISIBILITY_CRUD, false);
    router.push({ name: "Welcome" });
    return false
  }
  else {
    if (!permission) {
      getStore().commit(names.MUTATE_VISIBILITY_CRUD, false);
      return false
    } else {
      try {
        // SEC-3: los permisos ya no se persisten en localStorage — se leen del
        // store (rehidratado desde la API antes de montar la app en main.js).
        const permissions = getStore().state.permissions;
        const expressions = permission.split(' ');
        const allows_crud = getStore().state.utils.allows_crud;
        const findExpession = expressions.find(expression => permissions.includes(expression.replace(/'/g, "").replace(/"/g, "")));
        if (findExpession) getStore().commit(names.MUTATE_VISIBILITY_CRUD, true);
        else getStore().commit(names.MUTATE_VISIBILITY_CRUD, false);
        if (allows_crud && findExpession) {
          return allows_crud;
        }
        else {
          return false;
        }
      } catch (error) {
        return false;
      }
    }
  }
}

/**
 * Determina si un color es claro u oscuro basado en su valor RGB o HEX.
 * @param {string} color - El color en formato HEX o RGB.
 * @returns {boolean} - Devuelve true si el color es claro, false si es oscuro.
 */
export const lightOrDark = (color) => {
  // Variables for red, green, blue values
  var r, g, b, hsp;

  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If RGB --> store the red, green, blue values in separate variables
    color = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );

    r = color[1];
    g = color[2];
    b = color[3];
  } else {
    // If hex --> Convert it to RGB: http://gist.github.com/983661
    color = +(
      "0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&")
    );

    r = color >> 16;
    g = (color >> 8) & 255;
    b = color & 255;
  }

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 127.5) {
    return true; // return true if the colour is brighter
  } else {
    return false; // return false if the colour is darker
  }
}

/**
 * Convierte una cadena de HTML en texto plano.
 * @param {string} html - La cadena de HTML a convertir.
 * @returns {string} - El texto plano resultante del HTML.
 */
export const htmlToPlainText = (html, add_block_tags = false) => {
  // Crea un nuevo DOMParser
  const parser = new DOMParser();
  // Parsea el HTML a un nuevo documento
  const doc = parser.parseFromString(html, 'text/html');
  // Retorna el texto del documento, que será el texto plano
  if (add_block_tags) {
    // Reemplazar <br> por saltos de línea
    doc.body.querySelectorAll('br').forEach(br => {
      br.replaceWith('\n');
    });

    // Añadir salto de línea después de párrafos y otros bloques similares
    const blockTags = ['p', 'div', 'li', 'section', 'article', 'header', 'footer'];
    blockTags.forEach(tag => {
      doc.body.querySelectorAll(tag).forEach(el => {
        if (!el.textContent.endsWith('\n')) {
          el.textContent += '\n';
        }
      });
    });
    return doc.body.textContent.trim() || "";
  }
  return doc.body.textContent || "";
}

/**
 * Divide el HTML del editor en una entrada por línea: párrafo, ítem de lista o salto de línea.
 * @param {string} html - El HTML que devuelve el editor.
 * @returns {Array<{html: string, text: string}>} - Una entrada por línea con texto; las vacías se omiten.
 */
export const splitRichTextLines = (html) => {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Quill 2 agrega un span de control dentro de cada ítem de lista
  doc.body.querySelectorAll('.ql-ui').forEach(element => element.remove());

  const entries = [];
  const pushLines = (inner_html) => {
    inner_html.split(/<br\s*\/?>/i).forEach(line => {
      // Se reparsea cada trozo para cerrar las etiquetas que dejó abiertas el corte
      const container = doc.createElement('div');
      container.innerHTML = line;
      const text = container.textContent.trim();
      if (text != '') entries.push({ html: `<p>${container.innerHTML.trim()}</p>`, text: text });
    });
  };
  Array.from(doc.body.childNodes).forEach(node => {
    if (node.nodeType == Node.ELEMENT_NODE && ['UL', 'OL'].includes(node.tagName)) {
      node.querySelectorAll(':scope > li').forEach(item => pushLines(item.innerHTML));
    } else if (node.nodeType == Node.ELEMENT_NODE) {
      pushLines(node.innerHTML);
    } else if (node.nodeType == Node.TEXT_NODE) {
      // Texto suelto: se escapa antes de tratarlo como HTML
      const paragraph = doc.createElement('p');
      paragraph.textContent = node.textContent;
      pushLines(paragraph.innerHTML);
    }
  });
  return entries;
}

export const logOut = () => {
  // SEC-5: dispatch the action so the backend logout (revoke + must_relog) runs
  // with the token still present; it clears local state when done. Redirect now.
  // Bajar is_loged ANTES del redirect: el POST /logout/ es async y, si no, el
  // watcher de App.vue (isLoged && to == Welcome) rebota a DashboardView, que
  // queda en blanco cuando clearLoginData vacía el estado. El POST no depende
  // de is_loged (el Bearer sale de jwt_token, que sigue intacto).
  getStore().commit('setIsLoged', false);
  getStore().dispatch('logOut');
  if (!public_routes.map(x => x.name).includes(router.currentRoute.name) || router.currentRoute.name == 'Page404') {
    router.push({ name: "Welcome" });
  }
}

/**
 * Descarga un archivo desde una URL usando blob para evitar abrir nuevas ventanas/pestañas.
 * Usa window.location.href como fallback para evitar problemas de CORS.
 * 
 * @param {string} fileUrl - La URL del archivo a descargar.
 * @param {function} [onSuccess] - Callback opcional que se ejecuta cuando la descarga es exitosa.
 * @param {function} [onError] - Callback opcional que se ejecuta cuando hay un error en la descarga.
 */
export const downloadFileByUrl = (fileUrl, onSuccess = null, onError = null) => {
  try {
    // Extraer el nombre del archivo de la URL
    const fileName = fileUrl.split('/').pop().split('?')[0] || 'download';
    const extension = fileName.split('.').pop().toLowerCase();
    // Tipos de archivo que se deben abrir en nueva pestaña en lugar de descargar
    const viewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'pdf'];
    // Crear enlace temporal
    const link = document.createElement('a');
    link.href = fileUrl;
    // Si es imagen o PDF, abrir en nueva pestaña; si no, forzar descarga
    if (viewableTypes.includes(extension)) {
      link.target = '_blank';
    } else {
      link.download = fileName; // Forzar descarga
      link.target = '_self';
    }

    // Agregar al DOM temporalmente
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Error al descargar archivo:', error);

    // Fallback: usar window.location.href
    try {
      window.location.href = fileUrl;
      if (onSuccess) onSuccess();
    } catch (fallbackError) {
      console.error('Error en fallback de descarga:', fallbackError);
      if (onError) onError(fallbackError);
    }
  }
}

/**
 * Descarga un archivo desde un blob/response data usando createObjectURL.
 * Maneja la creación del blob, la descarga y limpieza automática de URLs temporales.
 * Extrae el nombre del archivo del header Content-Disposition si está disponible.
 * 
 * @param {object} response - La respuesta completa de axios que contiene data y headers
 * @param {string} [mimeType] - El tipo MIME del archivo (opcional, se detecta automáticamente)
 * @param {function} [onSuccess] - Callback opcional que se ejecuta cuando la descarga es exitosa
 * @param {function} [onError] - Callback opcional que se ejecuta cuando hay un error en la descarga
 */
export const downloadFileFromResponse = (response, mimeType = null, onSuccess = null, onError = null) => {
  try {
    const blobData = response.data;

    // Extraer el nombre del archivo del header Content-Disposition
    let fileName = 'archivo_descargado';

    // Debug: Verificar headers
    // console.log('Response headers:', response.headers);

    // Buscar Content-Disposition en diferentes variaciones
    const contentDisposition = response.headers['content-disposition'] ||
      response.headers['Content-Disposition'] ||
      response.headers['CONTENT-DISPOSITION'];

    // console.log('Content-Disposition header:', contentDisposition);

    if (contentDisposition) {
      // Mejorar la regex para capturar mejor el filename
      const fileNameMatch = contentDisposition.match(/filename\s*=\s*"?([^";\n]+)"?/i);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1].trim();
        // console.log('Extracted fileName:', fileName);
      }
      //   else {
      //     console.log('No fileName match found in Content-Disposition');
      //   }
    }
    //  else {
    //   console.log('No Content-Disposition header found');
    // }

    // Crear el blob si no es ya un blob
    let blob;
    if (blobData instanceof Blob) {
      blob = blobData;
    } else {
      // Auto-detectar tipo MIME común basado en extensión si no se proporciona
      if (!mimeType) {
        const extension = fileName.split('.').pop().toLowerCase();
        const mimeTypes = {
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'xls': 'application/vnd.ms-excel',
          'pdf': 'application/pdf',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'doc': 'application/msword',
          'csv': 'text/csv',
          'txt': 'text/plain',
          'json': 'application/json',
          'xml': 'application/xml'
        };
        mimeType = mimeTypes[extension] || 'application/octet-stream';
      }
      blob = new Blob([blobData], { type: mimeType });
    }

    // Crear URL temporal para el blob
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    // Ejecutar la descarga
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Limpiar la URL temporal
    window.URL.revokeObjectURL(url);

    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Error al descargar archivo desde blob:', error);
    if (onError) onError(error);
  }
}

/**
 * Imprime un reporte HTML en una nueva ventana con estilos predefinidos.
 * Abre una ventana de impresión con el contenido del reporte estilizado.
 *
 * @param {Object} config - Configuración del reporte
 * @param {string} config.title - Título del reporte que aparecerá en el header
 * @param {string} config.subtitle - Subtítulo opcional del reporte (ej: nombre de carrera)
 * @param {string} config.content - Contenido HTML del reporte a imprimir
 * @param {string} [config.updateTime] - Fecha/hora de generación del reporte (opcional)
 * @param {Object} [config.customStyles] - Estilos CSS personalizados adicionales (opcional)
 *
 * @example
 * newBlankPrint({
 *   title: 'Reporte de Coherencia',
 *   subtitle: 'Ingeniería en Informática',
 *   content: document.getElementById('reportContent').innerHTML,
 *   updateTime: new Date().toLocaleString('es-ES')
 * });
 */
export const newBlankPrint = (config) => {
  const {
    title,
    subtitle = '',
    content,
    updateTime = new Date().toLocaleString('es-ES'),
    showDate = true,
    requestedBy = '',
    customStyles = {},
    useDefaultStyles = true,
    openPrintDialog = true
  } = config;

  // Crear una ventana de impresión con solo el contenido del reporte
  const printWindow = window.open('', '_blank');

  // Estilos CSS por defecto
  const defaultStyles = useDefaultStyles ? `
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    @media print {
      @page {
        margin: 2cm !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    body {
      font-family: Arial, sans-serif !important;
      line-height: 1.6 !important;
      color: #333 !important;
      max-width: 900px !important;
      margin: 0 auto !important;
      padding: 20px !important;
      background-color: white !important;
    }
    h1 {
      color: #2c3e50 !important;
      border-bottom: 3px solid #3498db !important;
      padding-bottom: 10px !important;
      margin-bottom: 20px !important;
    }
    h2 {
      color: #34495e !important;
      margin: 10px 0 !important;
    }
    .section-header {
      color: #2c3e50 !important;
      border-bottom: 3px solid #3498db !important;
      padding-bottom: 0.5rem !important;
      margin-top: 1.5rem !important;
      margin-bottom: 1rem !important;
      font-size: 1.3em !important;
      font-weight: 600 !important;
    }
    .info-box {
      background-color: #e3f2fd !important;
      border-left: 4px solid #3498db !important;
      padding: 0.5rem;
      margin: 1rem 0 !important;
      border-radius: 4px !important;
    }
    .highlight {
      background-color: #fff9c4 !important;
      border-radius: 3px !important;
      font-weight: 600 !important;
      color: #f57c00 !important;
    }
    .warning {
      background-color: #fff3e0 !important;
      color: #e65100 !important;
      border-radius: 3px !important;
      font-weight: 500 !important;
      border: 1px solid #ffb74d !important;
    }
    .success {
      background-color: #e8f5e9 !important;
      color: #2e7d32 !important;
      border-radius: 3px !important;
      font-weight: 500 !important;
      border: 1px solid #81c784 !important;
    }
    ul {
      list-style-type: none !important;
      padding-left: 0 !important;
      margin: 0.5rem 0 !important;
    }
    ul li {
      padding: 0.5rem 0.5rem 0.5rem 2rem !important;
      position: relative !important;
      margin: 0.3rem 0 !important;
      line-height: 1.6 !important;
    }
    ul li:before {
      content: "▸" !important;
      position: absolute !important;
      left: 0.5rem !important;
      color: #3498db !important;
      font-weight: bold !important;
      font-size: 1.2em !important;
    }
    h3, h4 {
      margin-top: 1.2rem !important;
      margin-bottom: 0.8rem !important;
      color: #34495e !important;
    }
    h4 {
      font-size: 1.1em !important;
      font-weight: 500 !important;
      color: #546e7a !important;
    }
    p {
      margin: 0.8rem 0 !important;
      text-align: justify !important;
      color: #333 !important;
    }
    .print-header {
      text-align: center !important;
      margin-bottom: 30px !important;
    }
    .print-date {
      color: #7f8c8d !important;
      font-size: 0.9em !important;
      margin-top: 10px !important;
    }
    strong {
      color: #495057 !important;
      font-weight: 600 !important;
    }
  ` : '';

  const finalStyles = defaultStyles + (customStyles.css || '');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}${subtitle ? ' - ' + subtitle : ''}</title>
        <style>${finalStyles}</style>
      </head>
      <body>
        <div class="print-header">
          <h1>${title}</h1>
          ${subtitle ? `<h2>${subtitle}</h2>` : ''}
          ${showDate ? `<div class="print-date">
            ${requestedBy ? `Solicitado por: <strong>${requestedBy}</strong> &bull; ` : ''}Generado: ${updateTime}
          </div>` : ''}
        </div>
        ${content}
      </body>
    </html>
  `);

  printWindow.document.close();

  if (openPrintDialog) {
    // Esperar a que se cargue el contenido y luego abrir el diálogo de impresión
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  }
}